import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  ApiDownloadButton,
  ApiPostButton,
  ContentLabel,
  ContentValue,
  DisplayFormattedDate,
  MoreOptions,
  SendToEmailButton
} from 'components/common';
import {
  AnswersProgress,
  CourseProgressStatus,
  ReadingProgress,
  TaskPointsProgress,
  VideoProgress
} from 'components/common/tasks';
import {
  CourseLastOpened,
  CoursePathDeadlines,
  CourseUserDeadline
} from 'components/courses/common';

import type {
  Course,
  CourseProgress as CourseProgressData,
  RelatedPathDeadline
} from 'shared-types';

interface Props {
  allowDownloadingCertificate: boolean;
  course: Pick<
    Course,
    | 'certificateAllowDownloading'
    | 'id'
    | 'language'
    | 'languageVersions'
    | 'slug'
    | 'title'
  >;
  deadline?: string;
  lastOpenedDate?: string;
  progressData: CourseProgressData;
  relatedPathDeadlines?: RelatedPathDeadline[];
  userId: number;
}

const ProfileCourseProgress = ({
  allowDownloadingCertificate,
  course,
  deadline,
  lastOpenedDate,
  progressData,
  relatedPathDeadlines,
  userId
}: Props) => {
  const {
    language,
    messages,
    theme,
    user: loggedInUser,
    serverConfig
  } = useReduxState();

  const valueStyle = {
    color: theme.textColorLighter,
    fontSize: '1.5rem',
    fontWeight: 600
  };

  return (
    <div className="progress">
      <div className="data">
        <div className="data-items">
          <ContentLabel
            horizontal={true}
            label={messages.commonStatus}
            spacing={20}
          >
            <ContentValue>
              <CourseProgressStatus
                courseSlug={course.slug}
                id="course-progress-status"
                course={course}
                progressData={progressData}
                showCompletedVersionPreview={true}
              />
            </ContentValue>
          </ContentLabel>

          <ContentLabel
            horizontal={true}
            label={messages.courseLastOpened}
            spacing={20}
          >
            <ContentValue style={valueStyle}>
              <CourseLastOpened
                analyticsDisabledForAdmin={
                  progressData.analyticsDisabledForAdmin
                }
                lastOpenedDate={lastOpenedDate}
              />
            </ContentValue>
          </ContentLabel>

          {deadline || relatedPathDeadlines?.length ? (
            <ContentLabel
              horizontal={true}
              label={messages.courseTargetSchedule}
              spacing={20}
            >
              <ContentValue style={valueStyle}>
                {deadline ? (
                  <CourseUserDeadline
                    deadline={deadline}
                    isActive={
                      !Boolean(progressData?.completion?.status === 'success')
                    }
                    showIcon={true}
                    style={valueStyle}
                  />
                ) : relatedPathDeadlines?.length ? (
                  <CoursePathDeadlines
                    courseId={course.id}
                    deadlineStyle={valueStyle}
                    isActive={
                      !Boolean(progressData?.completion?.status === 'success')
                    }
                    pathDeadlines={relatedPathDeadlines}
                    userId={userId}
                  />
                ) : null}
              </ContentValue>
            </ContentLabel>
          ) : null}
        </div>
        <div className="data-items">
          {progressData?.completion?.completionDate ? (
            <ContentLabel
              horizontal={true}
              label={messages.completionsCompletionDate}
              spacing={20}
            >
              <ContentValue style={valueStyle}>
                <DisplayFormattedDate
                  date={progressData.completion.completionDate}
                  withTime={serverConfig.analyticsDatesDisplayTime}
                />
              </ContentValue>
            </ContentLabel>
          ) : null}

          {progressData?.completion?.expirationDate ? (
            <ContentLabel
              horizontal={true}
              label={messages.commonExpirationDate}
              spacing={20}
            >
              <ContentValue style={valueStyle}>
                <DisplayFormattedDate
                  date={progressData.completion.expirationDate}
                />
              </ContentValue>
            </ContentLabel>
          ) : null}

          {allowDownloadingCertificate &&
          progressData?.completion?.status === 'success' ? (
            <ContentLabel
              horizontal={true}
              label={messages.commonCertificate}
              style={{ whiteSpace: 'nowrap' }}
            >
              <ApiDownloadButton
                category="link-with-background"
                filename={`${course.title}.pdf`}
                icon={['far', 'file-pdf']}
                iconStyle={{
                  color: '#F40F02',
                  fontSize: '1.4rem',
                  top: 0
                }}
                size="header"
                style={{ borderBottomRightRadius: 0, borderTopRightRadius: 0 }}
                text={messages.completionsDownloadCertificate}
                url={
                  `/admin/certificate` +
                  `?completionId=${progressData.completion.id}` +
                  `&language=${language}`
                }
              />

              <MoreOptions
                buttonStyle={{
                  borderBottomLeftRadius: 0,
                  borderTopLeftRadius: 0,
                  marginLeft: 1
                }}
                category="lighter"
                styleContentButtons={false}
              >
                {loggedInUser?.email ? (
                  <ApiPostButton
                    data={{
                      completionId: progressData.completion.id,
                      emailAddress: loggedInUser.email,
                      language
                    }}
                    icon="envelope"
                    isDropdown={true}
                    successMessage={messages.completionsSendCertificateSuccess}
                    text={messages.completionsSendCertificate}
                    url="/admin/certificate/send"
                  />
                ) : (
                  <SendToEmailButton
                    apiData={{
                      completionId: progressData.completion.id,
                      language
                    }}
                    apiUrl="/admin/certificate/send"
                    buttonText={messages.completionsSendCertificate}
                    id="certificate"
                    isDropdown={true}
                    successMessage={messages.completionsSendCertificateSuccess}
                  />
                )}
              </MoreOptions>
            </ContentLabel>
          ) : null}
        </div>
      </div>

      <div className="stats">
        <div className="stat">
          <ReadingProgress
            analyticsDisabledForAdmin={progressData.analyticsDisabledForAdmin}
            readingProgress={progressData.readingProgress}
            readingProgressMaxTime={progressData.readingProgressMaxTime}
            showLabelTitle={true}
          />
        </div>

        <div className="stat">
          <VideoProgress
            analyticsDisabledForAdmin={progressData.analyticsDisabledForAdmin}
            numberOfVideos={progressData.numberOfVideos}
            showLabelTitle={true}
            videoProgress={progressData.videoProgress}
            videosWatched={progressData.videoProgressVideosWatched}
          />
        </div>

        <div className="stat">
          <AnswersProgress
            numberOfAnswers={progressData.taskAnswers}
            numberOfTasks={progressData.numberOfTasks}
            showLabelTitle={true}
          />
        </div>

        <div className="stat">
          <TaskPointsProgress
            numberOfTasks={progressData.numberOfTasks}
            showLabelTitle={true}
            taskMaxPoints={progressData.taskMaxPoints}
            taskPoints={progressData.taskPoints}
            taskPointsProgress={progressData.taskPointsProgress}
          />
        </div>
      </div>

      <style jsx>{`
        .progress {
          width: 100%;
        }
        .data {
          display: flex;
          flex-direction: column;
          gap: 40px 0px;
        }
        .stats {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-top: 40px;
        }
        .stat {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.cardBorderRadius};
          padding: 10px;
          width: 100%;
        }
        @media (min-width: ${breakpoints.small}px) {
          .stat {
            width: calc(50% - 10px);
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .data {
            flex-direction: row;
          }
          .data-items {
            width: calc(50% - 10px);
          }
          .stat {
            width: calc(23.333% - 15px);
          }
          .stat:last-child {
            width: calc(30% - 15px);
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileCourseProgress;
